import * as ClientContextStore from '@aon/aon-tws-common-ui/build/lib/components/ClientContext/ClientContextStore';
import { HierarchyGraphReducer, HierarchySearchReducer } from '@aon/tws-common-ui-hierarchy';
import { IHasHierarchyState } from '@aon/tws-common-ui-hierarchy/build/lib/types';
import { User } from 'oidc-client';
import { RouterState } from 'react-router-redux';
import { AnyAction, Reducer } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import * as AudienceDetailsStore from './AudienceDetailsStore';
import * as AudienceListStore from './AudienceListStore';
import * as BenchmarksStore from './BenchmarksStore';
import branchingLogicAttributesReducer, { IBranchingLogicAttributesState } from './BranchingLogicAttributesSlice';
import branchingLogicHierarchiesReducer, { IBranchingLogicHierarchiesState } from './BranchingLogicHierarchiesSlice';
import branchingLogicHierarchyReducer from './BranchingLogicHierarchySlice';
import * as BreakoutsStore from './BreakoutsStore';
import * as ClientParticipantAttributeStore from './ClientParticipantAttributeStore';
import * as CommonHierarchyStore from './CommonHierarchyStore';
import * as CommonQuestionListStore from './CommonQuestionListStore';
import * as ConfirmScheduleStore from './ConfirmScheduleStore';
import * as CulturesStore from './CulturesStore';
import * as CustomQuestionListStore from './CustomQuestionListStore';
import * as CustomQuestionModalStore from './CustomQuestionModalStore';
import * as CustomQuestionStore from './CustomQuestionStore';
import * as DistributionContactStore from './DistributionContactStore';
import * as DistributionFileStore from './DistributionFileStore';
import * as DistributionHierarchyStore from './DistributionHierarchyStore';
import * as EmailStore from './EmailStore';
import emailTemplateReducer, { IEmailTemplateState } from './EmailTemplateSlice';
import * as ExternalHierarchyStore from './ExternalHierarchyStore';
import * as GroupAccessOwnersStore from './GroupAccessOwners/ViewStore';
import * as HierarchyReportingAccessStore from './HierarchyReportingAccessStore';
import * as LibraryQuestionListStore from './LibraryQuestionListStore';
import * as NotificationStore from './NotificationStore';
import PendingParticipantsSlice, { IPendingParticipantsState } from './PendingParticipantsSlice';
import questionBranchingLogicReducer, { IQuestionBranchingLogicState } from './QuestionBranchingLogicSlice';
import * as QuestionChangesStore from './QuestionChangesStore';
import * as QuestionDisplayLogicModalStore from './QuestionDisplayLogicModalStore';
import { IQuestionDisplayLogicModalState } from './QuestionDisplayLogicModalStore';
import * as QuestionModalStore from './QuestionModalStore';
import * as ReportingAccessStore from './ReportingAccessStore';
import * as ReportingProfileStore from './ReportingProfileStore';
import * as ReportingSetupStore from './ReportingSetupStore';
import responseOptionGroupReducer, { IResponseOptionGroupState } from './ResponseOptionGroupSlice';
import * as SurveyAdminStore from './SurveyAdminStore';
import * as SurveyBuilderStore from './SurveyBuilderStore';
import SurveyContentItemsSlice from './SurveyContentItemsSlice';
import * as SurveyCopyModalStore from './SurveyCopyModalStore';
import SurveyDefinitionsSlice from './SurveyDefinitionsSlice';
import surveyDimensionReducer, { ISurveyDimensionsState } from './SurveyDimensionsSlice';
import surveyImageReducer, { ISurveyImageState } from './SurveyImageSlice';
import surveyInstructionsAdminReducer, { ISurveyInstructionsAdminState } from './SurveyInstructionsAdminSlice';
import surveyIntroAdminReducer, { ISurveyIntroAdminState } from './SurveyIntroAdminSlice';
import * as SurveyListStore from './SurveyListStore';
import * as SurveyParticipantStore from './SurveyParticipantStore';
import surveyPrivacyPolicyReducer, { ISurveyPrivacyPolicyState } from './SurveyPrivacyPolicySlice';
import surveyQuestionsAdminReducer, { ISurveyQuestionsAdminState } from './SurveyQuestionsAdminSlice';
import * as SurveyStore from './SurveyStore';
import surveyTemplateReducer, { ISurveyTemplateState } from './SurveyTemplateSlice';
import surveyThemeReducer, { ISurveyThemeState } from './SurveyThemeSlice';
import surveyTitleAdminReducer, { ISurveyTitleAdminState } from './SurveyTitleAdminSlice';
import * as TextToSpeechCultureStore from './TextToSpeechCultureStore';
import themeReducer, { IThemeState } from './ThemeSlice';
import * as TranslationEditorStore from './TranslationEditorStore';
import * as TrendGroupStore from './TrendGroupStore';

interface IOidcState {
	isLoadingUser: boolean;
	user?: User;
}

export type TThunkDispatch = ThunkDispatch<IApplicationState, unknown, AnyAction>;
export type TThunkAction<R, A extends AnyAction = AnyAction> = ThunkAction<R, IApplicationState, unknown, A>;

/* **************************************************************************** */
/* IMPORTANT!!!!
   The names of the RouterState and the reducer MUST match exactly. */
/* **************************************************************************** */

// The top-level state object
export interface IApplicationState extends RouterState, IHasHierarchyState {
	audienceDetails: AudienceDetailsStore.IAudienceDetailsState;
	audienceList: AudienceListStore.IAudienceListState;
	benchmarks: BenchmarksStore.IBenchmarksState;
	breakouts: BreakoutsStore.IBreakoutState;
	clientContextList: ClientContextStore.IClientContextState;
	clientParticipantAttributes: ClientParticipantAttributeStore.IClientParticipantAttributeState;
	commonQuestionList: CommonQuestionListStore.IQuestionListState;
	confirmSchedule: ConfirmScheduleStore.IConfirmScheduleState;
	cultures: CulturesStore.ICulturesState;
	customQuestion: CustomQuestionStore.ICustomQuestionState;
	customQuestionsModal: CustomQuestionModalStore.ICustomQuestionModalState;
	customQuestionList: CustomQuestionListStore.ICustomQuestionListState;
	distributionContacts: DistributionContactStore.IDistributionContactState;
	distributionHierarchy: DistributionHierarchyStore.IDistributionHierarchyState;
	distributionList: DistributionFileStore.IDistributionFileState;
	email: EmailStore.IEmailState;
	emailTemplates: IEmailTemplateState;
	externalHierarchy: ExternalHierarchyStore.IExternalHierarchyState;
	GroupAccessOwnerViews: GroupAccessOwnersStore.IGroupAccessOwnerViews;
	Hierarchy: CommonHierarchyStore.TCommonHierarchyState;
	hierarchyReportingAccess: HierarchyReportingAccessStore.IHierarchyReportingAccessState;
	libraryQuestions: LibraryQuestionListStore.ILibraryQuestionListState;
	notifications: NotificationStore.INotificationState;
	oidc: IOidcState;
	questionDisplayLogic: IQuestionDisplayLogicModalState;
	questionBranchingLogic: IQuestionBranchingLogicState;
	questionBranchingLogicAttributes: IBranchingLogicAttributesState;
	questionBranchingLogicHierarchies: IBranchingLogicHierarchiesState;
	pendingParticipants: IPendingParticipantsState;
	questionChanges: QuestionChangesStore.IQuestionChangeState;
	questionModal: QuestionModalStore.IQuestionModalState;
	reportingAccess: ReportingAccessStore.IReportingAccessState;
	reportingSetup: ReportingSetupStore.IReportingSetupState;
	reportingProfile: ReportingProfileStore.IReportingProfileState;
	responseOptionGroup: IResponseOptionGroupState;
	survey: SurveyStore.ISurveyState;
	surveyAdmin: SurveyAdminStore.ISurveyAdminState;
	surveyTitleAdmin: ISurveyTitleAdminState;
	surveyIntroAdmin: ISurveyIntroAdminState;
	surveyInstructionsAdmin: ISurveyInstructionsAdminState;
	surveyQuestionsAdmin: ISurveyQuestionsAdminState;
	surveyBuilder: SurveyBuilderStore.ISurveyBuilderState;
	surveyCopyModal: SurveyCopyModalStore.ISurveyCopyModalState;
	surveyDimensions: ISurveyDimensionsState;
	surveyImage: ISurveyImageState;
	surveyList: SurveyListStore.ISurveyListState;
	surveyParticipants: SurveyParticipantStore.ISurveyParticipantState;
	surveyPrivacyPolicy: ISurveyPrivacyPolicyState;
	surveyTemplates: ISurveyTemplateState;
	surveyTheme: ISurveyThemeState;
	textToSpeechCultures: TextToSpeechCultureStore.ITextToSpeechCultureState;
	themes: IThemeState;
	translations: TranslationEditorStore.ITranslationEditorState;
	trendGroup: TrendGroupStore.ITrendGroupState;
}
// Helper function that will allow multiple reducers run on the same slice of state.
const reduceReducers = (...reducerArray: Reducer[]): Reducer => {
	return (state, action) => {
		reducerArray.forEach((reducer) => {
			state = reducer(state, action);
		});
		return state;
	};
};

// Whenever an action is dispatched, Redux will update each top-level application state property using
// the reducer with the matching name. It's important that the names match exactly, and that the reducer
// acts on the corresponding ApplicationState property type.
export const reducers = {
	surveyList: SurveyListStore.reducer,
	survey: SurveyStore.reducer,
	cultures: CulturesStore.reducer,
	benchmarks: BenchmarksStore.reducer,
	breakouts: BreakoutsStore.reducer,
	questionModal: QuestionModalStore.reducer,
	libraryQuestions: LibraryQuestionListStore.reducer,
	customQuestionList: CustomQuestionListStore.reducer,
	customQuestionsModal: CustomQuestionModalStore.reducer,
	customQuestion: CustomQuestionStore.reducer,
	translations: TranslationEditorStore.reducer,
	notifications: NotificationStore.reducer,
	audienceList: AudienceListStore.reducer,
	audienceDetails: AudienceDetailsStore.reducer,
	reportingSetup: ReportingSetupStore.reducer,
	confirmSchedule: ConfirmScheduleStore.reducer,
	surveyAdmin: SurveyAdminStore.reducer,
	surveyTitleAdmin: surveyTitleAdminReducer,
	surveyIntroAdmin: surveyIntroAdminReducer,
	surveyInstructionsAdmin: surveyInstructionsAdminReducer,
	SurveyDefinitions: SurveyDefinitionsSlice,
	surveyQuestionsAdmin: surveyQuestionsAdminReducer,
	surveyParticipants: SurveyParticipantStore.reducer,
	email: EmailStore.reducer,
	emailTemplates: emailTemplateReducer,
	GroupAccessOwnerViews: GroupAccessOwnersStore.reducer,
	surveyBuilder: SurveyBuilderStore.reducer,
	distributionList: DistributionFileStore.reducer,
	distributionHierarchy: DistributionHierarchyStore.reducer,
	distributionContacts: DistributionContactStore.reducer,
	clientParticipantAttributes: ClientParticipantAttributeStore.reducer,
	reportingAccess: ReportingAccessStore.reducer,
	clientContextList: ClientContextStore.reducer,
	trendGroup: TrendGroupStore.reducer,
	surveyDimensions: surveyDimensionReducer,
	externalHierarchy: ExternalHierarchyStore.reducer,
	Hierarchy: reduceReducers(CommonHierarchyStore.reducer, HierarchyGraphReducer, HierarchySearchReducer),
	hierarchyReportingAccess: HierarchyReportingAccessStore.reducer,
	commonQuestionList: CommonQuestionListStore.reducer,
	questionChanges: QuestionChangesStore.reducer,
	questionDisplayLogic: QuestionDisplayLogicModalStore.reducer,
	questionBranchingLogic: reduceReducers(questionBranchingLogicReducer, branchingLogicHierarchyReducer),
	questionBranchingLogicAttributes: branchingLogicAttributesReducer,
	questionBranchingLogicHierarchies: branchingLogicHierarchiesReducer,
	reportingProfile: ReportingProfileStore.reducer,
	responseOptionGroup: responseOptionGroupReducer,
	surveyImage: surveyImageReducer,
	textToSpeechCultures: TextToSpeechCultureStore.reducer,
	surveyCopyModal: SurveyCopyModalStore.reducer,
	surveyPrivacyPolicy: surveyPrivacyPolicyReducer,
	surveyTemplates: surveyTemplateReducer,
	surveyTheme: surveyThemeReducer,
	themes: themeReducer,
	surveyContentItems: SurveyContentItemsSlice,
	pendingParticipants: PendingParticipantsSlice,
};
